/**
 * User Dropdown Menu with Stacking Context Fix
 *
 * PROBLEM:
 * The original dropdown was experiencing z-index issues where despite having
 * high z-index values (9999+), it was still appearing behind ads and other
 * page content. This is due to CSS stacking context limitations.
 *
 * ROOT CAUSE:
 * When an element creates a new stacking context (via position, z-index,
 * transform, opacity, etc.), all descendant elements are contained within
 * that stacking context, regardless of their z-index values.
 *
 * SOLUTION:
 * Move the dropdown completely outside of any container stacking contexts
 * by appending it directly to document.body with position: fixed and
 * precise JavaScript positioning. This ensures it can appear above all
 * page content including ads with high z-index values.
 *
 * @author InterPals Dev Team
 * @since 2024
 */

document.addEventListener('DOMContentLoaded', function() {
    /**
     * Initialize the user dropdown menu system
     * Creates dropdown HTML dynamically and appends to body to avoid stacking context issues
     */
    function initializeUserDropdown() {
        const toggleButton = document.getElementById('userDropdownToggle');
        const dropdownIcon = document.getElementById('userDropdownIcon');

        if (!toggleButton || !dropdownIcon) {
            return; // Elements not found, likely not on a page with user dropdown
        }

        // Extract user data from button for dynamic URL generation
        const userData = extractUserData();
        if (!userData) {
            console.warn('User dropdown: Could not extract user data');
            return;
        }

        // Create and inject dropdown HTML into document.body
        const dropdownMenu = createDropdownHTML(userData);
        document.body.appendChild(dropdownMenu);

        // Initialize dropdown functionality
        setupDropdownInteractions(toggleButton, dropdownMenu, dropdownIcon);
        setupLayoutToggle(dropdownMenu);
        updateNotificationBadge(toggleButton);
    }

    /**
     * Extract user data from the page for dropdown URLs
     * @returns {Object|null} User data object or null if not found
     */
    function extractUserData() {
        const userIdMeta = document.querySelector('meta[name="user-id"]');
        const userId = userIdMeta ? userIdMeta.getAttribute('content') : null;

        const toggleButton = document.getElementById('userDropdownToggle');
        const username = toggleButton ? toggleButton.getAttribute('data-username') : null;

        if (!userId || !username) {
            return null;
        }

        return {
            id: userId,
            username: username
        };
    }

    /**
     * Create the dropdown HTML element with all menu items
     * @param {Object} userData - User data for generating URLs
     * @returns {HTMLElement} The dropdown menu element
     */
    function createDropdownHTML(userData) {
        const dropdown = document.createElement('div');
        dropdown.id = 'userDropdownMenu';
        dropdown.className = 'min-w-64 max-w-80 origin-top-right bg-white rounded-lg shadow-xl ring-1 ring-black ring-opacity-5 focus:outline-none py-2 border border-gray-200';

        // CRITICAL: Use position: fixed and high z-index to break out of stacking contexts
        dropdown.style.cssText = `
            display: none;
            position: fixed;
            z-index: 999999;
            word-wrap: break-word;
            overflow-wrap: break-word;
        `;

        // Generate menu items
        const menuItems = [
            { href: `/${userData.username}`, icon: 'fa-user', label: 'My Profile', color: 'bg-blue-500' },
            { href: '/app/friends', icon: 'fa-users', label: 'Friends', color: 'bg-green-500' },
            { href: '/app/bookmarks', icon: 'fa-bookmark', label: 'Bookmarks', color: 'bg-cyan-500' },
            { href: '/app/albums', icon: 'fa-images', label: 'Photos', color: 'bg-purple-500' },
            { href: `/app/${userData.id}/posts`, icon: 'fa-file-lines', label: 'Posts', color: 'bg-amber-500' },
            { href: '/app/settings', icon: 'fa-gear', label: 'Settings', color: 'bg-gray-500' }
        ];

        let html = '';

        // Add regular menu items
        menuItems.forEach(item => {
            html += createMenuItemHTML(item);
        });

        // Add layout toggle
        html += `
            <div class="border-t border-gray-100 my-2 mx-3"></div>
            <div class="mx-2">
                <button id="layoutToggleInDropdown" class="w-full flex items-start px-3 py-3 text-gray-700 text-sm border border-transparent hover:border-gray-200 hover:bg-gray-50 hover:rounded-md transition-all duration-100 ease-in-out cursor-pointer text-left max-w-full">
                    <div class="bg-purple-500 rounded-full p-1.5 w-8 h-8 flex items-center justify-center mr-3 flex-shrink-0">
                        <i class="fa-solid fa-display text-white text-sm"></i>
                    </div>
                    <div class="flex-1 min-w-0 overflow-hidden">
                        <span id="layoutToggleTextInDropdown" class="block leading-snug">Classic Layout</span>
                        <div class="text-xs text-gray-500 mt-0.5 leading-snug">Switch layout style</div>
                    </div>
                </button>
            </div>
        `;

        // Add logout
        html += `
            <div class="border-t border-gray-100 my-2 mx-3"></div>
            ${createMenuItemHTML({ href: '/app/auth/logout', icon: 'fa-right-from-bracket', label: 'Sign Out', color: 'bg-red-500' })}
        `;

        dropdown.innerHTML = html;
        return dropdown;
    }

    /**
     * Create HTML for a single menu item
     * @param {Object} item - Menu item configuration
     * @returns {string} HTML string for the menu item
     */
    function createMenuItemHTML(item) {
        return `
            <a href="${item.href}" class="flex items-center px-5 py-3 text-gray-700 text-sm border border-transparent hover:border-gray-200 hover:bg-gray-50 hover:rounded-md transition-all duration-100 ease-in-out mx-2">
                <div class="${item.color} rounded-full p-1.5 w-8 h-8 flex items-center justify-center mr-3 flex-shrink-0">
                    <i class="fa-solid ${item.icon} text-white text-sm"></i>
                </div>
                <div class="flex-1 min-w-0">${item.label}</div>
            </a>
        `;
    }

    /**
     * Setup dropdown show/hide interactions and positioning
     * @param {HTMLElement} toggleButton - The dropdown toggle button
     * @param {HTMLElement} dropdownMenu - The dropdown menu element
     * @param {HTMLElement} dropdownIcon - The dropdown chevron icon
     */
    function setupDropdownInteractions(toggleButton, dropdownMenu, dropdownIcon) {
        let isPositioned = false;

        /**
         * Position the dropdown relative to the toggle button
         * Uses a flag to prevent repositioning during hover events which could cause jumping
         */
        function positionDropdown() {
            if (isPositioned) return; // Prevent re-positioning during hover events

            const buttonRect = toggleButton.getBoundingClientRect();

            // Position dropdown below and to the right of the button
            dropdownMenu.style.top = (buttonRect.bottom + 8) + 'px';
            dropdownMenu.style.right = (window.innerWidth - buttonRect.right) + 'px';

            // Ensure dropdown doesn't go off screen
            const dropdownRect = dropdownMenu.getBoundingClientRect();
            if (dropdownRect.left < 0) {
                dropdownMenu.style.right = '8px';
            }

            isPositioned = true;
        }

        /**
         * Show the dropdown menu
         */
        function showDropdown() {
            isPositioned = false; // Reset positioning flag
            dropdownMenu.style.display = 'block';
            dropdownMenu.style.visibility = 'visible';
            dropdownMenu.style.opacity = '1';
            dropdownIcon.style.transform = 'rotate(180deg)';
            positionDropdown();
        }

        /**
         * Hide the dropdown menu
         */
        function hideDropdown() {
            dropdownMenu.style.display = 'none';
            dropdownMenu.style.visibility = 'hidden';
            dropdownMenu.style.opacity = '0';
            dropdownIcon.style.transform = 'rotate(0deg)';
            isPositioned = false;
        }

        /**
         * Check if dropdown is currently visible
         * @returns {boolean} True if dropdown is visible
         */
        function isDropdownVisible() {
            return dropdownMenu.style.display === 'block';
        }

        // Toggle dropdown on button click
        toggleButton.addEventListener('click', function(e) {
            e.preventDefault();
            e.stopPropagation();

            if (isDropdownVisible()) {
                hideDropdown();
            } else {
                showDropdown();
            }
        });

        // Close dropdown when clicking outside and prevent unintended clicks
        document.addEventListener('click', function(e) {
            if (isDropdownVisible() &&
                !toggleButton.contains(e.target) &&
                !dropdownMenu.contains(e.target)) {

                // Prevent the click from propagating to underlying elements (like ads)
                e.preventDefault();
                e.stopPropagation();
                e.stopImmediatePropagation();

                hideDropdown();

                // Return false to further prevent any default actions
                return false;
            }
        }, true); // Use capture phase to intercept clicks before they reach other elements

        // Also add a backup listener for the bubbling phase
        document.addEventListener('click', function(e) {
            if (isDropdownVisible() &&
                !toggleButton.contains(e.target) &&
                !dropdownMenu.contains(e.target)) {
                hideDropdown();
            }
        });

        // Reposition dropdown on window resize if visible
        window.addEventListener('resize', function() {
            if (isDropdownVisible()) {
                isPositioned = false;
                positionDropdown();
            }
        });

        // Close dropdown on escape key
        document.addEventListener('keydown', function(e) {
            if (e.key === 'Escape' && isDropdownVisible()) {
                hideDropdown();
                toggleButton.focus(); // Return focus to toggle button
            }
        });
    }

    /**
     * Setup layout toggle functionality within the dropdown
     * Supports cycling through 2 layouts: sidebar <-> ultra-legacy
     * Uses localStorage as source of truth, synced to cookie for server-side rendering
     * @param {HTMLElement} dropdownMenu - The dropdown menu element
     */
    function setupLayoutToggle(dropdownMenu) {
        const layoutToggleBtn = dropdownMenu.querySelector('#layoutToggleInDropdown');
        const layoutToggleText = dropdownMenu.querySelector('#layoutToggleTextInDropdown');

        if (!layoutToggleBtn || !layoutToggleText) {
            return;
        }

        /**
         * Get the current layout from data attribute (cookie is httpOnly so JS can't read it)
         * @returns {string} Current layout: 'sidebar', 'classic', or 'ultra-legacy'
         */
        function getCurrentLayout() {
            // Try data attribute first
            if (layoutToggleBtn && layoutToggleBtn.dataset.currentLayout) {
                return layoutToggleBtn.dataset.currentLayout;
            }
            return 'sidebar';
        }

        /**
         * Get the next layout in the cycle
         * @param {string} currentLayout - Current layout
         * @returns {string} Next layout in cycle
         */
        function getNextLayout(currentLayout) {
            // Simple toggle between sidebar and ultra-legacy
            return currentLayout === 'ultra-legacy' ? 'sidebar' : 'ultra-legacy';
        }

        /**
         * Get display text for the next layout
         * @param {string} nextLayout - The layout to switch to
         * @returns {string} Display text
         */
        function getLayoutDisplayText(nextLayout) {
            const displayNames = {
                'sidebar': 'New Layout',
                'ultra-legacy': 'Classic Layout'
            };
            return displayNames[nextLayout] || 'Switch Layout';
        }

        function updateToggleText() {
            const currentLayout = getCurrentLayout();
            const nextLayout = getNextLayout(currentLayout);
            layoutToggleText.textContent = getLayoutDisplayText(nextLayout);
        }

        // Initialize toggle text
        updateToggleText();

        // Handle toggle click
        layoutToggleBtn.addEventListener('click', function(e) {
            e.preventDefault();

            const currentLayout = getCurrentLayout();
            const newLayout = getNextLayout(currentLayout);

            // Save to localStorage and redirect
            if (window.setLayoutPreference) {
                window.setLayoutPreference(newLayout);
            }
            window.location.href = window.location.pathname + '?layout=' + newLayout;
        });
    }

    /**
     * Update notification badge on the toggle button
     * @param {HTMLElement} toggleButton - The dropdown toggle button
     */
    function updateNotificationBadge(toggleButton) {
        // This function can be extended to handle dynamic notification updates
        // For now, the badge is handled in the Twig template with server-side data

        // Future enhancement: Listen for real-time notification updates
        // and update the badge accordingly
    }

    // Initialize the dropdown system
    initializeUserDropdown();
});
